import React, {
  createContext, FC, useCallback, useContext, useEffect,
} from 'react';
import { useAuth0, RedirectLoginOptions } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { IdleTimerProvider } from 'react-idle-timer';
import { decodeJwt } from '../../utils/jwt';
import { JWT_LOCAL_STORAGE_KEY } from '../../constants/constants';
import { environment } from '../../environments/environment';
import { ParentProps } from '../../models';
import { usePatientContext } from '../patient-context';
import localStorage from '../../services/localStorage';

import { AuthAccessTokenDecoded, AuthContextValues } from './model';
import { useLoadingSpinnerContext } from '../loading-spinner-context';

export const AuthContext = createContext<AuthContextValues>({} as AuthContextValues);

/** For readability: Minutes * 60 * 1000 */
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * The goal of this provider is to abstract the Auth0 layer
 * that is defined in an upper level.
 */
interface Auth0ProviderProps extends ParentProps {
  idlenessTimeout?: number;
}

export const AuthProvider: FC<Auth0ProviderProps> = (props) => {
  const {
    children,
    idlenessTimeout = IDLE_TIMEOUT_MS,
  } = props;

  const [searchParams] = useSearchParams();

  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const {
    setShowingLoadingSpinner,
  } = useLoadingSpinnerContext();

  const { getPatient } = usePatientContext();

  const getUserMetadata = async () => {
    try {
      const accessToken = await getAccessTokenSilently({
        audience: environment.auth0Audience,
        ignoreCache: false,
      });

      localStorage.set(JWT_LOCAL_STORAGE_KEY, accessToken);
      const accessTokenDecoded = decodeJwt<AuthAccessTokenDecoded>(accessToken);
      if (accessTokenDecoded) {
        const patientId = accessTokenDecoded['https://studykik.com/studykik_id'];
        getPatient(patientId);
      }
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    if (!environment.disableAuth0 && !isLoading && !isAuthenticated) {
      const loginParams: RedirectLoginOptions = {
        redirectUri: window.location.origin,
        audience: environment.auth0Audience,
      };

      const invitationFlag = searchParams.get('invitation');
      const organizationFlag = searchParams.get('organization');

      if (invitationFlag !== null) {
        loginParams.invitation = invitationFlag;
      }
      if (organizationFlag !== null) {
        loginParams.organization = organizationFlag;
      }

      loginWithRedirect(loginParams);
    } else if (isAuthenticated) {
      getUserMetadata();
    } else if (environment.disableAuth0 === true) {
      getPatient('test');
    }
  }, [isLoading, isAuthenticated, searchParams]);

  useEffect(() => {
    setShowingLoadingSpinner(
      'auth',
      !environment.disableAuth0 && (isLoading || !isAuthenticated),
    );
  }, [isLoading, isAuthenticated]);

  const handleIdleness = useCallback(() => {
    localStorage.remove(JWT_LOCAL_STORAGE_KEY);
    logout({ returnTo: window.location.origin });
  }, []);

  const value: AuthContextValues = {
    isLogged: isAuthenticated,
    isLoading,
    logout: () => {
      logout({ returnTo: window.location.origin });
    },
    user,
  };

  return (
    <AuthContext.Provider
      value={value}
    >
      <IdleTimerProvider
        timeout={idlenessTimeout}
        onIdle={handleIdleness}
      >
        {children}
      </IdleTimerProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
